import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import useLoginCheck from "../util/useLoginCheck";
import Spinner from "../util/Spinner";
import { fetchTVShowList } from "../slice/tvShowListsSlice";
import { fetchTVShowListItems, setPage } from "../slice/tvShowListItemsSlice";
import { fetchRatings } from "../slice/ratingSlice";
import { fetchTVShowGenres } from "../slice/tvShowGenreSlice";
import { fetchTVShowLanguages } from "../slice/tvShowLanguageSlice";

import TVShowListItemRow, { TVShowListItemCard } from "./TVShowListItemRow";

const TVShowList = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  // params from url
  const { sid } = useParams();

  // tv show list
  const { tvShowList, isLoading } = useSelector((state) => {
    return state?.tvShowLists;
  });
  // tv show list items
  const { content, page, totalPages, totalElements, isLoadingItems } =
    useSelector((state) => {
      console.log("items: ", state?.tvShowListItems);
      return state?.tvShowListItems;
    });
  const [shouldRefresh, setShouldRefresh] = useState(true);
  const ratings = useSelector((state) => state.ratings.ratings);
  const genres = useSelector((state) => state.tvShowGenres.tvShowGenres);
  const languages = useSelector(
    (state) => state.tvShowLanguages.tvShowLanguages
  );

  // filters
  const [ratingId, setRatingId] = useState(-1);
  const [ratingDescription, setRatingDescription] = useState("");
  const [genreId, setGenreId] = useState(-1);
  const [genreName, setGenreName] = useState("");
  const [genre, setGenre] = useState("");
  const [language, setLanguage] = useState("");

  const navigate = useNavigate();

  useLoginCheck();

  useEffect(() => {
    dispatch(fetchRatings());
    dispatch(fetchTVShowGenres());
    dispatch(fetchTVShowLanguages());
  }, [dispatch]);

  const handleRatingSelected = (event) => {
    const value = event.target.value;
    console.log("handleRatingSelected value ", value);
    const rating = parseInt(value, 10); // convert to integer in base 10
    const selectedRating = ratings.find((r) => r.value === rating);
    console.log("selected rating ", selectedRating);
    setRatingId(selectedRating?.value);
    if (selectedRating == null) {
      setRatingDescription("");
    } else {
      setRatingDescription(selectedRating?.description);
    }
  };

  const handleGenreSelected = (event) => {
    const value = event.target.value;
    console.log("handleGenreSelected value ", value);
    const genre = parseInt(value, 10); // convert to integer in base 10
    const selectedGenre = genres.find((r) => r.value === genre);
    console.log("selected genre ", selectedGenre);
    setGenreId(selectedGenre?.value);
    if (selectedGenre == null) {
      setGenreName("");
    } else {
      setGenreName(selectedGenre?.description);
    }
  };

  const handleLanguageSelected = (event) => {
    const value = event.target.value;
    console.log("handleLanguageSelected value ", value);
    setLanguage(value);
    if (value == null) {
      setLanguage("");
    }
  };

  // fetch the tv show list object
  useEffect(() => {
    if (sid) {
      console.log("useEffect calling fetch tv show list with sid ", sid);
      dispatch(fetchTVShowList(sid));
    }
  }, [dispatch, sid, page, shouldRefresh]);

  // fetch the items associated with the tv show list object
  useEffect(() => {
    if (sid) {
      console.log("useEffect calling fetch tv show list items with sid ", sid);
      dispatch(
        fetchTVShowListItems({
          sid,
          rating: ratingDescription,
          genre: genreName,
          language,
          page,
          size: 30,
        })
      );
    }
  }, [
    dispatch,
    sid,
    ratingDescription,
    language,
    genreName,
    page,
    shouldRefresh,
  ]);

  const addTVShow = () => {
    navigate(`${location.pathname}/add`);
  };

  // pagination handlers
  const handleNext = () => {
    if (page < totalPages - 1) {
      dispatch(setPage(page + 1));
    }
  };

  const handlePrevious = () => {
    if (page > 0) {
      dispatch(setPage(page - 1));
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4">
      {isLoading ? (
        <Spinner />
      ) : (
        <div>
          {tvShowList ? (
            <div>
              <div className="flex items-start justify-between gap-4">
                {/* headers, filters */}
                <div>
                  <div className="text-lg font-semibold text-stone-800">
                    {tvShowList?.name}
                  </div>
                  <div className="text-xs font-semibold text-stone-500">
                    {tvShowList?.type}
                  </div>
                  <div className="text-xs font-semibold text-teal-700">
                    {totalElements} shows
                  </div>
                </div>
                <button className="btn-primary shrink-0" onClick={addTVShow}>
                  Add
                </button>
              </div>

              {/* filters */}
              <div className="flex flex-wrap gap-4 mt-4">
                <div className="flex flex-col">
                  <label htmlFor="rating-select" className="text-sm font-medium text-stone-500 mb-1">
                    Rating
                  </label>
                  <select
                    id="rating-select"
                    value={ratingId}
                    onChange={handleRatingSelected}
                    className="border border-stone-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-600 bg-white"
                  >
                    <option value="">None</option>
                    {ratings.map((rating) => (
                      <option value={rating?.value}>
                        {rating?.description}
                      </option>
                    ))}
                  </select>
                </div>

                {/* genre */}
                <div className="flex flex-col">
                  <label htmlFor="genre-select" className="text-sm font-medium text-stone-500 mb-1">
                    Genre
                  </label>
                  <select
                    id="genre-select"
                    value={genreId}
                    onChange={handleGenreSelected}
                    className="border border-stone-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-600 bg-white"
                  >
                    <option value="">None</option>
                    {genres.map((genre) => (
                      <option value={genre?.value}>{genre?.description}</option>
                    ))}
                  </select>
                </div>

                {/* language */}
                <div className="flex flex-col">
                  <label htmlFor="language-select" className="text-sm font-medium text-stone-500 mb-1">
                    Language
                  </label>
                  <select
                    id="language-select"
                    value={language}
                    onChange={handleLanguageSelected}
                    className="border border-stone-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-600 bg-white"
                  >
                    <option value="">None</option>
                    {languages.map((language) => (
                      <option value={language}>{language}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-stone-500">No TV shows entered</p>
          )}
        </div>
      )}
      {isLoadingItems ? (
        <Spinner />
      ) : (
        <>
          {content?.length > 0 ? (
            <div className="mt-4">
              <table className="hidden md:table w-full card">
                <colgroup>
                  <col style={{ width: "65%" }} />
                  <col style={{ width: "15%" }} />
                  <col style={{ width: "20%" }} />
                </colgroup>
                <tbody className="divide-y divide-stone-200">
                  {content?.map((item) => {
                    return (
                      <TVShowListItemRow
                        key={item?.sid}
                        tvShowListItem={item}
                      ></TVShowListItemRow>
                    );
                  })}
                </tbody>
              </table>
              <ul className="md:hidden space-y-3">
                {content?.map((item) => (
                  <TVShowListItemCard key={item?.sid} tvShowListItem={item} />
                ))}
              </ul>

              {/* pagination */}
              <div className="flex items-center justify-center gap-4 mt-4">
                <button
                  onClick={handlePrevious}
                  disabled={page === 0}
                  className="btn-secondary min-h-11 px-4"
                >
                  {"<"}
                </button>
                <span className="text-sm text-stone-600">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  onClick={handleNext}
                  disabled={page >= totalPages - 1}
                  className="btn-secondary min-h-11 px-4"
                >
                  {">"}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-stone-500 mt-4">No TV shows on your list!</p>
          )}
        </>
      )}
    </div>
  );
};

export default TVShowList;
